import {Component, EventEmitter, Input, OnInit, Output, ViewContainerRef, ChangeDetectorRef} from '@angular/core';
import {DataImportedReview, EntityImportedData, TransactionProfileImportedData, PersonVO, OrganisationVO} from '../dataImported.model';
import {CurrencyService} from '../../../shared/services/currency-service';
import {GenericSearchService} from '../../../shared/components/genericsearch/generic-search.service';
import {ConstantsService} from '../../../services/constants-service';
import {MdDialog, MdDialogConfig, MdDialogRef} from '@angular/material';
import {ConfirmationDialogComponent} from '../../../shared/components/confirmationdialog/confirmation-dialog.component';
import {FilterCounterpartiesDialogComponent} from './filter-counterparties-dialog.component';
import {CreateCounterpartyDialogComponent} from './create-counterparty-dialog.component';
import {ImportController} from '../import.controller';
import {SnackBarService} from '../../../services/snackbar.service';
import {EntityTransactionProfileService} from '../../../entities/transaction-profile/entity-transaction-profile.service';
import {Agreement} from '../../../entities/transaction-profile/agreement.model';
import {Observable} from 'rxjs/Observable';

@Component({
  selector: 'app-review-import',
  templateUrl: './review-import.component.html',
  styleUrls: ['./review-import.component.css'],
  providers: [GenericSearchService]
})
export class ReviewImportComponent implements OnInit {

  @Input() importData: DataImportedReview;
  @Input() onMappingChange?: () => void; // Callback for when mappings change
  @Output() saveData = new EventEmitter<DataImportedReview>();
  @Output() importDataOutput = new EventEmitter<DataImportedReview>(); // Emit when import is triggered
  @Output() mappingChange = new EventEmitter<DataImportedReview>(); // Emit when mappings change
  selectedClient: EntityImportedData;
  currencies: any[];
  
  // Client search functionality
  clientSearchResults: any[] = [];
  selectedClientMapping: Map<number, any> = new Map(); // Track client mappings
  
  // Entity search functionality for counterparties
  selectedEntityMapping: Map<string, any> = new Map(); // Track entity mappings (key: entity identifier)
  
  // Counterparty search for transaction profiles
  counterpartySearchResults: Map<string, any[]> = new Map(); // Track search results per profile (key: profile identifier)
  
  // Extracted counterparties from transaction profiles
  extractedCounterparties: EntityImportedData[] = [];
  
  // Transaction profiles sorting
  transactionProfilesSortField: string = 'clientName';
  transactionProfilesSortOrder: number = 1; // 1 for ascending, -1 for descending
  
  // Counterparties sorting
  counterpartiesSortField: string = 'excelCounterpartyName';
  counterpartiesSortOrder: number = 1; // 1 for ascending, -1 for descending
  cachedCounterparties: EntityImportedData[] = []; // Cached list for sorting
  counterpartiesTableFirst: number = 0; // Store current page for counterparties table
  
  // Bank accounts sorting
  bankAccountsSortField: string = 'accountName';
  bankAccountsSortOrder: number = 1; // 1 for ascending, -1 for descending
  cachedBankAccounts: any[] = []; // Cached list for sorting with entity info
  
  // Auto-mapping state
  isAutoMapping: boolean = false;
  
  dialogRef: MdDialogRef<any>;

  constructor(
    private currencyService: CurrencyService,
    private genericSearchService: GenericSearchService,
    private dialog: MdDialog,
    private viewContainerRef: ViewContainerRef,
    private importController: ImportController,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private entityTransactionProfileService: EntityTransactionProfileService
  ) { }

  ngOnInit() {
    this.loadCurrencies();
    // Initialize excel client names if not set
    if (this.importData && this.importData.entityImportedDataList) {
      this.importData.entityImportedDataList.forEach(entity => {
        if (!entity.excelClientName && entity.organisation) {
          entity.excelClientName = entity.organisation.name;
        }
      });
    }
    // Validate counterparties in transaction profiles and mark as (NOT FOUND) if needed
    this.validateCounterpartiesInProfiles();
    // Initialize status property for transaction profiles (for sorting)
    this.initializeTransactionProfileStatus();
    // Cache counterparties list
    this.updateCachedCounterparties();
    // Cache bank accounts list
    this.updateCachedBankAccounts();
  }

  /**
   * Validate counterparties in transaction profiles - check if they exist in mapping
   * Note: We no longer add "(NOT FOUND)" suffix, just track for styling
   */
  private validateCounterpartiesInProfiles() {
    if (!this.importData || !this.importData.transactionProfiles || !this.importData.entityImportedDataList) {
      return;
    }

    // Build a set of all entity names (from both clients and counterparties) for quick lookup
    const entityNames = new Set<string>();
    this.importData.entityImportedDataList.forEach(entity => {
      // Get display name for all entities
      const entityName = this.getEntityDisplayName(entity);
      if (entityName) {
        entityNames.add(entityName.trim().toLowerCase());
      }
      // Also check excelClientName for clients (important for matching)
      if (entity.excelClientName) {
        entityNames.add(entity.excelClientName.trim().toLowerCase());
      }
      // Also check organisation name if available
      if (entity.organisation && entity.organisation.name) {
        entityNames.add(entity.organisation.name.trim().toLowerCase());
      }
      // Also check person name if available
      if (entity.person) {
        const personName = (entity.person.firstNames && entity.person.lastNames ? 
          `${entity.person.firstNames} ${entity.person.lastNames}` : 
          entity.person.callingName);
        if (personName) {
          entityNames.add(personName.trim().toLowerCase());
        }
      }
    });

    // Check each transaction profile's counterparty - remove (NOT FOUND) suffix if present
    this.importData.transactionProfiles.forEach(profile => {
      if (profile.counterPartyName) {
        const counterpartyName = profile.counterPartyName.trim();
        // Remove "(NOT FOUND)" suffix if it exists
        const originalName = counterpartyName.replace(/\s*\(NOT FOUND\)\s*$/i, '').trim();
        if (counterpartyName !== originalName) {
          profile.counterPartyName = originalName;
        }
      }
    });
  }

  /**
   * Check if a counterparty name exists in the import mapping (clients or counterparties)
   */
  isCounterpartyNotFound(counterpartyName: string): boolean {
    if (!counterpartyName || !this.importData || !this.importData.entityImportedDataList) {
      return false;
    }
    
    // Ensure counterpartyName is a string
    if (typeof counterpartyName !== 'string') {
      return false;
    }
    
    const nameLower = counterpartyName.trim().toLowerCase();
    
    // Check if it exists in the mapping
    for (const entity of this.importData.entityImportedDataList) {
      const entityName = this.getEntityDisplayName(entity);
      if (entityName && entityName.trim().toLowerCase() === nameLower) {
        return false; // Found
      }
      if (entity.excelClientName && entity.excelClientName.trim().toLowerCase() === nameLower) {
        return false; // Found
      }
    }
    
    return true; // Not found
  }

  /**
   * Search for counterparties in transaction profiles - searches both clients and counterparties from import mapping
   */
  searchCounterpartyForProfile(event: any, profile: TransactionProfileImportedData) {
    const query = event.query || '';
    const profileKey = this.getProfileKey(profile);
    
    if (!query || query.length < 2) {
      this.counterpartySearchResults.set(profileKey, []);
      return;
    }
    
    const queryLower = query.toLowerCase();
    const results: any[] = [];
    
    // Search in both clients and counterparties from import mapping
    if (this.importData && this.importData.entityImportedDataList) {
      this.importData.entityImportedDataList.forEach(entity => {
        const entityName = this.getEntityDisplayName(entity);
        const excelName = entity.excelClientName;
        
        // Check if name matches query
        if (entityName && entityName.toLowerCase().includes(queryLower)) {
          results.push({
            value: entityName,
            entityId: entity.organisation ? entity.organisation.entityId : (entity.person ? entity.person.entityId : null),
            isClient: entity.isClient === true,
            entity: entity,
            type: entity.organisation ? 'Organisation' : 'Person'
          });
        } else if (excelName && excelName.toLowerCase().includes(queryLower)) {
          results.push({
            value: excelName,
            entityId: entity.organisation ? entity.organisation.entityId : (entity.person ? entity.person.entityId : null),
            isClient: entity.isClient === true,
            entity: entity,
            type: entity.organisation ? 'Organisation' : 'Person'
          });
        }
      });
    }
    
    // Add "Create new" option
    results.push({
      value: `Create new: "${query}"`,
      isCreateNew: true,
      createName: query,
      type: 'create'
    });
    
    this.counterpartySearchResults.set(profileKey, results);
  }

  /**
   * Get search results for a transaction profile's counterparty
   */
  getCounterpartySearchResults(profile: TransactionProfileImportedData): any[] {
    const profileKey = this.getProfileKey(profile);
    return this.counterpartySearchResults.get(profileKey) || [];
  }

  /**
   * Get a unique key for a transaction profile
   */
  private getProfileKey(profile: TransactionProfileImportedData): string {
    return `${profile.clientName || ''}_${profile.counterPartyName || ''}_${profile.currencyIsoCode || ''}`;
  }

  /**
   * Handle counterparty selection from autocomplete
   */
  onCounterpartySelect(selectedResult: any, profile: TransactionProfileImportedData) {
    if (!selectedResult) {
      return;
    }
    
    // Check if user wants to create a new entity
    if (selectedResult.isCreateNew) {
      this.openCreateCounterpartyDialog(selectedResult.createName, profile);
      return;
    }
    
    // User selected an existing entity
    if (selectedResult.value) {
      profile.counterPartyName = selectedResult.value;
      
      // If the profile was enabled and the counterparty changed, we might need to update mappings
      if (profile.enabled) {
        // Check if the new counterparty is excluded
        const counterpartyEntity = selectedResult.entity;
        if (counterpartyEntity && counterpartyEntity.excludedFromImport === true) {
          // Disable the profile if counterparty is excluded
          profile.enabled = false;
          this.clearDomainMappings(profile);
        } else {
          // Regenerate mappings with the new counterparty name
          this.generateDomainMappings(profile);
        }
      }
      
      // Emit mapping change
      this.emitMappingChange();
    }
  }

  /**
   * Handle counterparty name change (when user types manually)
   */
  onCounterpartyNameChange(profile: TransactionProfileImportedData) {
    if (!profile.counterPartyName) {
      return;
    }

    // Remove (NOT FOUND) suffix if present
    const originalName = profile.counterPartyName.replace(/\s*\(NOT FOUND\)\s*$/i, '').trim();
    profile.counterPartyName = originalName;

    // If the profile was enabled and the counterparty changed, we might need to update mappings
    if (profile.enabled) {
      // Check if the new counterparty is excluded
      const counterpartyEntity = this.findCounterpartyEntityByName(originalName);
      if (counterpartyEntity && counterpartyEntity.excludedFromImport === true) {
        // Disable the profile if counterparty is excluded
        profile.enabled = false;
        this.clearDomainMappings(profile);
      } else {
        // Regenerate mappings with the new counterparty name
        this.generateDomainMappings(profile);
      }
    }

    // Emit mapping change
    this.emitMappingChange();
  }

  /**
   * Open dialog to create a new counterparty entity
   */
  openCreateCounterpartyDialog(name: string, profile: TransactionProfileImportedData) {
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    config.width = '450px';
    config.disableClose = false;
    config.data = {
      name: name
    };

    const dialogRef = this.dialog.open(CreateCounterpartyDialogComponent, config);
    
    // Set the name property on the component instance
    dialogRef.componentInstance.name = name;
    
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.isOrganisation !== undefined) {
        this.createNewCounterpartyEntity(result.name, result.isOrganisation, profile);
      }
    });
  }

  /**
   * Create a new counterparty entity in the import mapping
   */
  createNewCounterpartyEntity(name: string, isOrganisation: boolean, profile: TransactionProfileImportedData) {
    // Check if entity with this name already exists
    const existingEntity = this.findCounterpartyEntityByName(name);
    if (existingEntity) {
      this.snackBarService.showSimpleMsj(`Counterparty "${name}" already exists in the mapping.`);
      profile.counterPartyName = name;
      this.emitMappingChange();
      return;
    }
    
    // Create new EntityImportedData
    let newEntity: EntityImportedData;
    
    if (isOrganisation) {
      newEntity = {
        organisation: {
          name: name,
          id: 0,
          entityId: 0,
          isin: '',
          legalValue: 0,
          stockExchangeValue: 0,
          incorporatedDate: null,
          registrationCode: '',
          logo: '',
          sanctionStatus: 'FALSE',
          specialFinancialInstitution: false,
          userId: 0,
          activityCategory: null,
          activities: [],
          activitySummary: '',
          clientId: 0,
          bankAccounts: [],
          addresses: []
        },
        person: null,
        isClient: false,
        exists: false,
        counterParties: [],
        bankAccounts: [],
        excludedFromImport: false,
        excelClientName: name // Store the name for reference
      };
    } else {
      // For person, split name into parts if possible
      const nameParts = name.trim().split(/\s+/);
      let personData: PersonVO;
      if (nameParts.length >= 2) {
        personData = {
          callingName: nameParts[0],
          firstNames: nameParts[0],
          lastNames: nameParts.slice(1).join(' '),
          middleNames: '',
          id: 0,
          entityId: 0,
          gender: 0,
          nationality: 0,
          birthday: null,
          ssn: '',
          logo: ''
        };
      } else {
        personData = {
          callingName: name,
          firstNames: '',
          lastNames: '',
          middleNames: '',
          id: 0,
          entityId: 0,
          gender: 0,
          nationality: 0,
          birthday: null,
          ssn: '',
          logo: ''
        };
      }
      
      newEntity = {
        person: personData,
        organisation: null,
        isClient: false,
        exists: false,
        counterParties: [],
        bankAccounts: [],
        excludedFromImport: false,
        excelClientName: name // Store the name for reference
      };
    }
    
    // Add to entity list
    if (!this.importData.entityImportedDataList) {
      this.importData.entityImportedDataList = [];
    }
    this.importData.entityImportedDataList.push(newEntity);
    
    // Update counterparty name in profile
    profile.counterPartyName = name;
    
    // Update cached counterparties
    this.updateCachedCounterparties();
    
    // Regenerate mappings if profile is enabled
    if (profile.enabled) {
      this.generateDomainMappings(profile);
    }
    
    // Emit mapping change
    this.emitMappingChange();
    
    this.snackBarService.showSimpleMsj(`Created new ${isOrganisation ? 'organisation' : 'person'} counterparty: "${name}"`);
  }

  /**
   * Initialize status property for transaction profiles to enable sorting
   * Status will be "Migrated" or "New" based on hasExistingMappings
   */
  private initializeTransactionProfileStatus() {
    if (this.importData && this.importData.transactionProfiles) {
      this.importData.transactionProfiles.forEach(profile => {
        this.updateProfileStatus(profile);
      });
    }
  }

  /**
   * Update the status property for a transaction profile (for sorting)
   */
  private updateProfileStatus(profile: TransactionProfileImportedData) {
    (profile as any).status = this.hasExistingMappings(profile) ? 'Migrated' : 'New';
  }

  save() {
    console.log('save => ', this.importData);
    
    // If counterparties have been extracted, rebuild the entity list
    if (this.extractedCounterparties && this.extractedCounterparties.length > 0) {
      console.log('Rebuilding entity list with extracted counterparties');
      this.rebuildEntityList();
    }
    
    this.saveData.emit(this.importData);
  }
  
  /**
   * Rebuild entity list with clients + extracted counterparties
   */
  private rebuildEntityList() {
    if (!this.importData || !this.importData.entityImportedDataList) {
      return;
    }
    
    // Get all clients from current entity list
    const clients: EntityImportedData[] = [];
    this.importData.entityImportedDataList.forEach(entity => {
      if (entity.isClient === true) {
        clients.push(entity);
      }
    });
    
    console.log('Clients to keep:', clients.length);
    console.log('Extracted counterparties to add:', this.extractedCounterparties.length);
    
    // Rebuild entity list: clients first, then extracted counterparties
    this.importData.entityImportedDataList = [...clients, ...this.extractedCounterparties];
    
    console.log('New entity list size:', this.importData.entityImportedDataList.length);
    this.emitMappingChange();
  }

  import() {
    console.log('import => ', this.importData);
    this.importDataOutput.emit(this.importData);
  }

  /**
   * Auto-map counterparties by calling backend service
   * Searches for matches and maps counterparties to existing entities
   */
  autoMapCounterparties() {
    if (!this.importData) {
      this.snackBarService.showSimpleMsj('No import data available');
      return;
    }

    this.isAutoMapping = true;
    this.snackBarService.showSimpleMsj('Starting auto-mapping of counterparties...');

    this.importController.autoMapCounterparties(this.importData).subscribe({
      next: (response) => {
        console.log('Auto-mapping completed:', response);
        this.isAutoMapping = false;
        
        if (response && response.dataReview) {
          // Update the import data with the mapped results
          this.importData = response.dataReview;
          this.updateCachedCounterparties();
          this.emitMappingChange();
          this.snackBarService.showSimpleMsj(`Auto-mapping completed. ${response.mappedCount || 0} counterparties mapped.`);
        } else {
          this.snackBarService.showSimpleMsj('Auto-mapping completed but no data returned.');
        }
      },
      error: (error) => {
        console.error('Error in auto-mapping:', error);
        this.isAutoMapping = false;
        const errorMsg = error._body || error.message || 'Failed to auto-map counterparties';
        this.snackBarService.showSimpleMsj('Auto-mapping failed: ' + errorMsg);
      }
    });
  }

  autoMapClients() {
    if (!this.importData) {
      this.snackBarService.showSimpleMsj('No import data available');
      return;
    }

    this.isAutoMapping = true;
    this.snackBarService.showSimpleMsj('Starting auto-mapping of clients...');

    this.importController.autoMapClients(this.importData).subscribe({
      next: (response) => {
        console.log('Client auto-mapping completed:', response);
        this.isAutoMapping = false;
        
        if (response && response.dataReview) {
          // Update the import data with the mapped results
          this.importData = response.dataReview;
          this.emitMappingChange();
          this.snackBarService.showSimpleMsj(`Client auto-mapping completed. ${response.mappedCount || 0} clients mapped.`);
        } else {
          this.snackBarService.showSimpleMsj('Client auto-mapping completed but no data returned.');
        }
      },
      error: (error) => {
        console.error('Error in client auto-mapping:', error);
        this.isAutoMapping = false;
        const errorMsg = error._body || error.message || 'Failed to auto-map clients';
        this.snackBarService.showSimpleMsj('Client auto-mapping failed: ' + errorMsg);
      }
    });
  }

  /**
   * Filter counterparties: disable those that are not clients and do not occur in any enabled transaction profile
   * Sets excludedFromImport = true for these counterparties instead of removing them
   */
  filterCounterpartiesNotInProfiles() {
    if (!this.importData) {
      this.snackBarService.showSimpleMsj('No import data available');
      return;
    }
    
    if (!this.importData.entityImportedDataList || !this.importData.transactionProfiles) {
      this.snackBarService.showSimpleMsj('Missing entity list or transaction profiles data');
      return;
    }

    // Extract unique counter party names from enabled transaction profiles only
    const usedCounterPartyNames = new Set<string>(); // Set of lowercase names
    
    this.importData.transactionProfiles.forEach(profile => {
      // Only consider enabled transaction profiles
      if (profile.enabled === true && profile.counterPartyName && profile.counterPartyName.trim() !== '') {
        const cpNameLower = profile.counterPartyName.trim().toLowerCase();
        usedCounterPartyNames.add(cpNameLower);
      }
    });

    // Get all client names (to exclude them from being disabled)
    const clientNames = new Set<string>();
    this.importData.entityImportedDataList.forEach(entity => {
      if (entity.isClient === true) {
        const clientName = entity.excelClientName || this.getEntityDisplayName(entity);
        if (clientName) {
          clientNames.add(clientName.trim().toLowerCase());
        }
      }
    });

    // Disable counterparties that are not clients and not in transaction profiles
    let disabledCount = 0;
    this.importData.entityImportedDataList.forEach(entity => {
      // Skip if it's a client
      if (entity.isClient === true) {
        return;
      }

      const entityName = this.getEntityDisplayName(entity);
      if (!entityName) {
        return;
      }

      const entityNameLower = entityName.trim().toLowerCase();
      
      // Check if this counterparty is used in any enabled transaction profile or is a client
      const isUsedInEnabledProfiles = usedCounterPartyNames.has(entityNameLower);
      const isClient = clientNames.has(entityNameLower);
      
      // If not used in enabled profiles and not a client, disable it
      if (!isUsedInEnabledProfiles && !isClient) {
        if (entity.excludedFromImport !== true) {
          entity.excludedFromImport = true;
          disabledCount++;
          console.log(`Disabled counterparty from import: "${entityName}" (not in enabled transaction profiles)`);
        }
      }
    });

    if (disabledCount > 0) {
      console.log(`Disabled ${disabledCount} counterparties from import (not in enabled transaction profiles)`);
      this.snackBarService.showSimpleMsj(`Filtered counterparties: ${disabledCount} counterparties disabled (not in enabled transaction profiles).`);
      this.emitMappingChange();
    } else {
      this.snackBarService.showSimpleMsj('All counterparties are used in enabled transaction profiles or are clients.');
    }
  }

  loadCurrencies () {
    this.currencyService.getCurrencies().subscribe(response => {
      console.log('currency response => ', response);
      this.currencies = response.arrResult;
    });
  }

  getExistingEntities(entities: EntityImportedData[]): number {
    if (!entities || !entities.length) {
      return 0;
    }
    return entities.filter(entity => entity.exists || entity.mappedClientId).length;
  }

  /**
   * Get all clients (entities where isClient = true), sorted alphabetically
   */
  getAllClients(): EntityImportedData[] {
    if (!this.importData || !this.importData.entityImportedDataList) {
      return [];
    }
    const clients = this.importData.entityImportedDataList.filter(entity => entity.isClient === true);
    
    // Sort alphabetically by display name
    clients.sort((a, b) => {
      const nameA = (a.excelClientName || this.getEntityDisplayName(a) || '').toLowerCase();
      const nameB = (b.excelClientName || this.getEntityDisplayName(b) || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return clients;
  }

  /**
   * Search for clients when user types in the search box
   */
  searchClient(event, entity: EntityImportedData) {
    const query = event.query || '';
    if (!query || query.length < 2) {
      // Clear results for this entity
      if (entity.organisation) {
        this.selectedClientMapping.set(entity.organisation.id, {
          entity: entity,
          results: []
        });
      }
      return;
    }
    this.genericSearchService.getHeaderSearchResults(query, ConstantsService.CLIENT_SEARCH)
      .subscribe(response => {
        const results = this.parseOrganisationSearchResponse(response.arrResult.organisations || []);
        // Store results for this specific entity
        if (entity.organisation) {
          this.selectedClientMapping.set(entity.organisation.id, {
            entity: entity,
            results: results
          });
        }
      });
  }

  /**
   * Handle client selection from search results
   */
  selectClient(selectedResult: any, entity: EntityImportedData) {
    if (selectedResult && selectedResult.clientId) {
      entity.mappedClientId = selectedResult.clientId;
      entity.mappedClientName = selectedResult.value;
      entity.searchQuery = ''; // Clear search query
      console.log('Mapped client:', entity.excelClientName, '->', entity.mappedClientName);
      
      // Trigger change detection to update counts
      this.updateCounts();
      
      // Emit mapping change event to save to session
      if (this.onMappingChange) {
        this.onMappingChange();
      }
      this.mappingChange.emit(this.importData);
    }
  }

  /**
   * Update counts display
   */
  private updateCounts() {
    // Force change detection by updating a dummy property
    // This ensures the template re-evaluates the count expressions
    if (this.importData && this.importData.entityImportedDataList) {
      // Trigger change detection
      setTimeout(() => {
        // This will cause Angular to re-evaluate the template expressions
      }, 0);
    }
  }

  /**
   * Clear client mapping
   */
  clearMapping(client: EntityImportedData) {
    client.mappedClientId = null;
    client.mappedClientName = null;
    
    // Trigger change detection to update counts
    this.updateCounts();
    
    // Emit mapping change event to save to session
    if (this.onMappingChange) {
      this.onMappingChange();
    }
    this.mappingChange.emit(this.importData);
  }

  /**
   * Move a client to the counterparties list by removing the isClient flag
   * This allows the entity to be mapped as a counterparty instead of a client
   */
  moveClientToCounterparties(client: EntityImportedData) {
    const clientName = client.excelClientName || this.getEntityDisplayName(client);
    
    // Check if this entity is referenced in any transaction profiles as a client
    const entityNameLower = clientName ? clientName.trim().toLowerCase() : '';
    const clientKey = 'CLIENT:' + entityNameLower;
    const entityKey = 'ENTITY:' + entityNameLower;
    
    let profilesAffected = 0;
    if (this.importData && this.importData.transactionProfiles) {
      profilesAffected = this.importData.transactionProfiles.filter(profile => 
        profile.clientEntityKey === clientKey || profile.counterPartyEntityKey === clientKey
      ).length;
    }
    
    // Build confirmation message
    let message = `Are you sure you want to move "${clientName}" from clients to counterparties?`;
    if (profilesAffected > 0) {
      message += `\n\nThis will affect ${profilesAffected} transaction profile(s) that reference this entity.`;
      message += ` The entity keys in those profiles will be updated to use the "ENTITY:" prefix instead of "CLIENT:".`;
    }
    message += `\n\nThis will clear all client mappings and allow you to map it as a counterparty instead.`;
    
    // Show confirmation dialog
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    config.width = '500px';
    config.data = {
      title: 'Move to Counterparties',
      message: message,
      confirmText: 'Move to Counterparties',
      cancelText: 'Cancel'
    };

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, config);
    
    this.dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Set isClient to false
        client.isClient = false;
        
        // Clear client mappings
        client.mappedClientId = null;
        client.mappedClientName = null;
        client.searchQuery = null;
        
        // Clear entity mappings to allow fresh mapping as counterparty
        client.mappedEntityId = null;
        client.mappedEntityName = null;
        client.entitySearchQuery = null;
        
        // Update transaction profiles that reference this entity
        if (this.importData && this.importData.transactionProfiles) {
          this.importData.transactionProfiles.forEach(profile => {
            // If this entity was referenced as a client, update to counterparty key
            if (profile.clientEntityKey === clientKey) {
              // This shouldn't happen - a client shouldn't be a client in a profile
              // But if it does, we'll update it to counterparty
              profile.counterPartyEntityKey = entityKey;
              profile.clientEntityKey = null;
            } else if (profile.counterPartyEntityKey === clientKey) {
              // Update counterparty key from CLIENT: to ENTITY:
              profile.counterPartyEntityKey = entityKey;
            }
          });
        }
        
        // Update cached counterparties (this will add the entity to the counterparties list)
        this.updateCachedCounterparties();
        
        // Trigger change detection to update counts
        this.updateCounts();
        
        // Emit mapping change event to save to session
        this.emitMappingChange();
        
        // Show success message
        let successMsg = `"${clientName}" has been moved to counterparties.`;
        if (profilesAffected > 0) {
          successMsg += ` Updated ${profilesAffected} transaction profile(s).`;
        }
        successMsg += ` You can now map it in the Counterparties tab.`;
        this.snackBarService.showSimpleMsj(successMsg);
      }
    });
  }

  /**
   * Display function for client name
   */
  displayClientName(result: any): string {
    return result ? result.value : '';
  }

  /**
   * Get search results for a specific entity
   */
  getSearchResultsForEntity(entity: EntityImportedData): any[] {
    if (entity.organisation) {
      const mapping = this.selectedClientMapping.get(entity.organisation.id);
      return mapping ? mapping.results : [];
    }
    return [];
  }


  /**
   * Parse organisation search response
   */
  private parseOrganisationSearchResponse(organisationResponse): any[] {
    const arrFormatOrgSearchResults: any[] = [];
    if (organisationResponse && organisationResponse.length > 0) {
      for (let resultCounter = 0; resultCounter < organisationResponse.length; resultCounter++) {
        const searchRecordData = organisationResponse[resultCounter];
        arrFormatOrgSearchResults.push({
          'value': searchRecordData.name,
          'id': searchRecordData.id,
          'clientId': searchRecordData.clientId,
          'entityId': searchRecordData.entityId
        });
      }
    }
    return arrFormatOrgSearchResults;
  }

  /**
   * Get count of enabled transaction profiles
   */
  getEnabledProfiles(): number {
    if (!this.importData || !this.importData.transactionProfiles) {
      return 0;
    }
    return this.importData.transactionProfiles.filter(profile => profile.enabled).length;
  }

  /**
   * Get count of disabled transaction profiles
   */
  getDisabledProfiles(): number {
    if (!this.importData || !this.importData.transactionProfiles) {
      return 0;
    }
    return this.importData.transactionProfiles.filter(profile => !profile.enabled).length;
  }

  /**
   * Check if a transaction profile has existing mappings (has been migrated before)
   */
  hasExistingMappings(profile: TransactionProfileImportedData): boolean {
    if (!profile) {
      return false;
    }
    
    // Check if any of the mapped IDs exist
    return !!(profile.agreement && profile.agreement.mappedAgreementId ||
             profile.obligation && profile.obligation.mappedObligationId ||
             profile.rights && profile.rights.mappedRightsId ||
             profile.outgoingTransactionRule && profile.outgoingTransactionRule.mappedTransactionRuleId ||
             profile.incomingTransactionRule && profile.incomingTransactionRule.mappedTransactionRuleId);
  }

  /**
   * Clear mapped data (agreement, obligation, rights, rules) to allow re-import
   * This deletes the entities from the database first, then removes the mapped IDs
   * NOTE: This does NOT delete counterparties or bank accounts - only the domain objects
   */
  clearMappedData(profile: TransactionProfileImportedData): void {
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    config.width = '400px';
    config.data = {
      title: 'Delete Mapped Data',
      message: `Are you sure you want to delete the mapped data for this transaction profile?\n\n` +
               `This will DELETE from the database:\n` +
               `• Agreement\n` +
               `• Obligation\n` +
               `• Rights\n` +
               `• Transaction Rules (outgoing and incoming)\n\n` +
               `The profile will be treated as new and can be imported again.\n\n` +
               `Note: Counterparties and bank accounts will NOT be affected.`,
      confirmText: 'Delete Entities',
      cancelText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.deleteMappedEntities(profile);
      }
    });
  }

  /**
   * Delete mapped entities from the database, then clear the mapped IDs
   * Handles errors gracefully - continues even if one entity deletion fails
   */
  private deleteMappedEntities(profile: TransactionProfileImportedData): void {
    const deleteOperations: Observable<{type: string, success: boolean, message?: string}>[] = [];

    // Delete Agreement
    if (profile.agreement && profile.agreement.mappedAgreementId) {
      const agreement = new Agreement();
      agreement.id = profile.agreement.mappedAgreementId;
      deleteOperations.push(
        this.entityTransactionProfileService.removeAgreement(agreement)
          .map(() => {
            return { type: 'agreement', success: true, message: 'Agreement' };
          })
          .catch(error => {
            // If entity doesn't exist (404) or other error, log but continue
            if (error.status === 404) {
              return Observable.of({ type: 'agreement', success: true, message: 'Agreement (already deleted)' });
            } else {
              return Observable.of({ 
                type: 'agreement', 
                success: false, 
                message: `Agreement (ID: ${profile.agreement.mappedAgreementId}): ${error.message || 'Unknown error'}` 
              });
            }
          })
      );
    }

    // Delete Obligation
    if (profile.obligation && profile.obligation.mappedObligationId) {
      deleteOperations.push(
        this.entityTransactionProfileService.removeObligation(profile.obligation.mappedObligationId)
          .map(() => {
            return { type: 'obligation', success: true, message: 'Obligation' };
          })
          .catch(error => {
            if (error.status === 404) {
              return Observable.of({ type: 'obligation', success: true, message: 'Obligation (already deleted)' });
            } else {
              return Observable.of({ 
                type: 'obligation', 
                success: false, 
                message: `Obligation (ID: ${profile.obligation.mappedObligationId}): ${error.message || 'Unknown error'}` 
              });
            }
          })
      );
    }

    // Delete Rights
    if (profile.rights && profile.rights.mappedRightsId) {
      deleteOperations.push(
        this.entityTransactionProfileService.removeRight(profile.rights.mappedRightsId)
          .map(() => {
            return { type: 'rights', success: true, message: 'Rights' };
          })
          .catch(error => {
            if (error.status === 404) {
              return Observable.of({ type: 'rights', success: true, message: 'Rights (already deleted)' });
            } else {
              return Observable.of({ 
                type: 'rights', 
                success: false, 
                message: `Rights (ID: ${profile.rights.mappedRightsId}): ${error.message || 'Unknown error'}` 
              });
            }
          })
      );
    }

    // Delete Outgoing Transaction Rule
    if (profile.outgoingTransactionRule && profile.outgoingTransactionRule.mappedTransactionRuleId) {
      deleteOperations.push(
        this.entityTransactionProfileService.removeTransactionRule(profile.outgoingTransactionRule.mappedTransactionRuleId)
          .map(() => {
            return { type: 'outgoingRule', success: true, message: 'Outgoing Transaction Rule' };
          })
          .catch(error => {
            if (error.status === 404) {
              return Observable.of({ type: 'outgoingRule', success: true, message: 'Outgoing Transaction Rule (already deleted)' });
            } else {
              return Observable.of({ 
                type: 'outgoingRule', 
                success: false, 
                message: `Outgoing Transaction Rule (ID: ${profile.outgoingTransactionRule.mappedTransactionRuleId}): ${error.message || 'Unknown error'}` 
              });
            }
          })
      );
    }

    // Delete Incoming Transaction Rule
    if (profile.incomingTransactionRule && profile.incomingTransactionRule.mappedTransactionRuleId) {
      deleteOperations.push(
        this.entityTransactionProfileService.removeTransactionRule(profile.incomingTransactionRule.mappedTransactionRuleId)
          .map(() => {
            return { type: 'incomingRule', success: true, message: 'Incoming Transaction Rule' };
          })
          .catch(error => {
            if (error.status === 404) {
              return Observable.of({ type: 'incomingRule', success: true, message: 'Incoming Transaction Rule (already deleted)' });
            } else {
              return Observable.of({ 
                type: 'incomingRule', 
                success: false, 
                message: `Incoming Transaction Rule (ID: ${profile.incomingTransactionRule.mappedTransactionRuleId}): ${error.message || 'Unknown error'}` 
              });
            }
          })
      );
    }

    if (deleteOperations.length === 0) {
      this.snackBarService.showSimpleMsj('No mapped entities found to delete.');
      return;
    }

    // Execute all delete operations in parallel
    Observable.forkJoin(deleteOperations).subscribe(
      results => {
        const deleted: string[] = [];
        const errors: string[] = [];

        // Collect results
        results.forEach(result => {
          if (result.success) {
            deleted.push(result.message);
          } else {
            errors.push(result.message);
          }
        });

        // Clear all mapped IDs regardless of deletion results
        if (profile.agreement) {
          profile.agreement.mappedAgreementId = undefined;
        }
        if (profile.obligation) {
          profile.obligation.mappedObligationId = undefined;
        }
        if (profile.rights) {
          profile.rights.mappedRightsId = undefined;
        }
        if (profile.outgoingTransactionRule) {
          profile.outgoingTransactionRule.mappedTransactionRuleId = undefined;
        }
        if (profile.incomingTransactionRule) {
          profile.incomingTransactionRule.mappedTransactionRuleId = undefined;
        }

        // If profile is disabled, regenerate mappings so they're ready for next import
        if (!profile.enabled && profile.agreement && profile.agreement.willCreate) {
          this.generateDomainMappings(profile);
        }

        // Update status property for sorting (mapped IDs have been cleared)
        this.updateProfileStatus(profile);

        // Emit mapping change to trigger save
        this.emitMappingChange();

        // Show summary message
        let message = '';
        if (deleted.length > 0) {
          message = `Deleted: ${deleted.join(', ')}. `;
        }
        if (errors.length > 0) {
          message += `Errors: ${errors.join('; ')}. `;
        }
        message += 'Mapped data cleared. The transaction profile can now be imported again.';
        this.snackBarService.showSimpleMsj(message);
      },
      error => {
        // This should not happen as we catch errors in individual operations
        console.error('Unexpected error during entity deletion:', error);
        this.snackBarService.showSimpleMsj('An unexpected error occurred during deletion. Please check the console for details.');
      }
    );
  }

  /**
   * Handle column sort for transaction profiles
   * PrimeNG will handle the actual sorting, we just update our state to reflect the current sort
   */
  onTransactionProfilesSort(event: any) {
    this.transactionProfilesSortField = event.field;
    // PrimeNG uses 1 for ascending, -1 for descending
    this.transactionProfilesSortOrder = event.order;
    
    console.log('Sorting transaction profiles by:', event.field, 'order:', event.order === 1 ? 'asc' : 'desc');
  }

  /**
   * Handle column sort for counterparties
   * PrimeNG will handle the actual sorting, we just update our state to reflect the current sort
   */
  onCounterpartiesSort(event: any) {
    // PrimeNG event can have 'field' or 'sortField' property
    const sortField = event.field || event.sortField;
    this.counterpartiesSortField = sortField;
    // PrimeNG uses 1 for ascending, -1 for descending
    this.counterpartiesSortOrder = event.order;
    
    console.log('Sorting counterparties by:', sortField, 'order:', event.order === 1 ? 'asc' : 'desc');
  }

  /**
   * Update profile mapping when enabled/disabled
   * Note: Changes are NOT automatically saved - user must click "Save Mappings" button
   */
  updateProfileMapping(profile: TransactionProfileImportedData, event: any) {
    const willBeEnabled = event.target.checked;
    
    if (willBeEnabled) {
      // Check if client or counterparty is excluded from import
      const clientEntity = this.findClientEntityByName(profile.clientName);
      const counterpartyEntity = this.findCounterpartyEntityByName(profile.counterPartyName);
      
      const excludedEntities: { entity: EntityImportedData, name: string, type: 'client' | 'counterparty', bankAccounts: any[] }[] = [];
      
      if (clientEntity && clientEntity.excludedFromImport === true) {
        const bankAccounts = clientEntity.bankAccounts || [];
        excludedEntities.push({
          entity: clientEntity,
          name: profile.clientName,
          type: 'client',
          bankAccounts: bankAccounts
        });
      }
      
      if (counterpartyEntity && counterpartyEntity.excludedFromImport === true) {
        const bankAccounts = counterpartyEntity.bankAccounts || [];
        excludedEntities.push({
          entity: counterpartyEntity,
          name: profile.counterPartyName,
          type: 'counterparty',
          bankAccounts: bankAccounts
        });
      }
      
      if (excludedEntities.length > 0) {
        // Prevent the checkbox from being checked
        event.preventDefault();
        event.stopPropagation();
        event.target.checked = false;
        profile.enabled = false;
        
        // Show confirmation dialog
        this.openEnableProfileWithExcludedEntitiesDialog(profile, excludedEntities);
        return;
      }
      
      // No excluded entities - proceed with enabling
      profile.enabled = true;
      // Generate mappings when user enables the profile, regardless of original isActive value
      this.generateDomainMappings(profile);
    } else {
      profile.enabled = false;
      this.clearDomainMappings(profile);
    }
    
    // Don't auto-save - changes will be saved when user clicks "Save Mappings" button
    // Only update counts for UI display
    this.updateCounts();
  }
  
  /**
   * Find client entity by name
   */
  private findClientEntityByName(clientName: string): EntityImportedData | null {
    if (!this.importData || !this.importData.entityImportedDataList || !clientName) {
      return null;
    }
    
    const clientNameLower = clientName.trim().toLowerCase();
    
    for (const entity of this.importData.entityImportedDataList) {
      if (entity.isClient === true) {
        const entityName = entity.excelClientName || this.getEntityDisplayName(entity);
        if (entityName && entityName.trim().toLowerCase() === clientNameLower) {
          return entity;
        }
      }
    }
    
    return null;
  }
  
  /**
   * Find counterparty entity by name (checks both clients and counterparties)
   */
  private findCounterpartyEntityByName(counterpartyName: string): EntityImportedData | null {
    if (!this.importData || !this.importData.entityImportedDataList || !counterpartyName) {
      return null;
    }
    
    // Remove (NOT FOUND) suffix if present
    const originalName = counterpartyName.replace(/\s*\(NOT FOUND\)\s*$/i, '').trim();
    const counterpartyNameLower = originalName.toLowerCase();
    
    // First check in clients
    for (const entity of this.importData.entityImportedDataList) {
      if (entity.isClient === true) {
        const entityName = entity.excelClientName || this.getEntityDisplayName(entity);
        if (entityName && entityName.trim().toLowerCase() === counterpartyNameLower) {
          return entity;
        }
      }
    }
    
    // Then check in counterparties
    for (const entity of this.importData.entityImportedDataList) {
      if (entity.isClient !== true) {
        const entityName = this.getEntityDisplayName(entity);
        if (entityName && entityName.trim().toLowerCase() === counterpartyNameLower) {
          return entity;
        }
      }
    }
    
    return null;
  }
  
  /**
   * Open confirmation dialog when enabling a profile with excluded client/counterparty
   */
  openEnableProfileWithExcludedEntitiesDialog(
    profile: TransactionProfileImportedData,
    excludedEntities: { entity: EntityImportedData, name: string, type: 'client' | 'counterparty', bankAccounts: any[] }[]
  ) {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;
    
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, config);
    
    // Build message
    let message = 'Enabling this transaction profile will also enable the import of the following ';
    if (excludedEntities.length === 1) {
      const excluded = excludedEntities[0];
      message += excluded.type === 'client' ? 'client' : 'counterparty';
      message += `: "${excluded.name}"`;
      
      // Add bank accounts if any
      if (excluded.bankAccounts && excluded.bankAccounts.length > 0) {
        message += `\n\nBank accounts that will be enabled:\n`;
        excluded.bankAccounts.forEach((account, index) => {
          const accountName = account.accountName || account.accountNumber || '(Unnamed account)';
          const bankName = account.bankInstitutionName || '';
          message += `${index + 1}. ${accountName}${bankName ? ' (' + bankName + ')' : ''}\n`;
        });
      }
      message += '.';
    } else {
      message += 'entities:\n';
      excludedEntities.forEach((excluded, index) => {
        message += `${index + 1}. ${excluded.type === 'client' ? 'Client' : 'Counterparty'}: "${excluded.name}"`;
        
        // Add bank accounts if any
        if (excluded.bankAccounts && excluded.bankAccounts.length > 0) {
          message += `\n   Bank accounts (${excluded.bankAccounts.length}): `;
          const accountNames = excluded.bankAccounts.map(account => 
            account.accountName || account.accountNumber || '(Unnamed)'
          ).join(', ');
          message += accountNames;
        }
        message += '\n';
      });
    }
    message += '\nDo you want to continue?';
    
    this.dialogRef.componentInstance.message = message;
    this.dialogRef.componentInstance.title = 'Enable Transaction Profile';
    
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // User confirmed - enable the profile and the excluded entities
        profile.enabled = true;
        
        // Enable all excluded entities (this automatically enables their bank accounts)
        excludedEntities.forEach(excluded => {
          excluded.entity.excludedFromImport = false;
        });
        
        // Update bank accounts cache since entity exclusion changed
        this.updateCachedBankAccounts();
        
        // Generate mappings
        this.generateDomainMappings(profile);
        
        // Update counts
        this.updateCounts();
      }
      // If cancelled, profile.enabled is already false, so nothing to do
    });
  }

  /**
   * Generate domain mappings based on Excel data
   */
  private generateDomainMappings(profile: TransactionProfileImportedData) {
    console.log('Generating domain mappings for profile:', {
      clientName: profile.clientName,
      paymentsMaximum: profile.paymentsMaximum,
      receiptMaximum: profile.receiptMaximum,
      paymentsTurnover: profile.paymentsTurnover,
      receiptTurnover: profile.receiptTurnover,
      paymentNatureDescription: profile.paymentNatureDescription,
      receiptNatureDescription: profile.receiptNatureDescription
    });

    // Preserve existing mapped IDs if they exist (profile was previously imported)
    const existingAgreementId = profile.agreement && profile.agreement.mappedAgreementId;
    const existingObligationId = profile.obligation && profile.obligation.mappedObligationId;
    const existingRightsId = profile.rights && profile.rights.mappedRightsId;
    const existingOutgoingRuleId = profile.outgoingTransactionRule && profile.outgoingTransactionRule.mappedTransactionRuleId;
    const existingIncomingRuleId = profile.incomingTransactionRule && profile.incomingTransactionRule.mappedTransactionRuleId;

    // Create Agreement - only include non-empty values
    const agreementParts: string[] = [];
    if (profile.counterPartyName && profile.counterPartyName.trim() !== '') {
      agreementParts.push(profile.counterPartyName.trim());
    }
    if (profile.paymentNatureDescription && profile.paymentNatureDescription.trim() !== '') {
      agreementParts.push(profile.paymentNatureDescription.trim());
    }
    if (profile.receiptNatureDescription && profile.receiptNatureDescription.trim() !== '') {
      agreementParts.push(profile.receiptNatureDescription.trim());
    }
    profile.agreement = {
      description: agreementParts.join(' || '),
      willCreate: true,
      mappedAgreementId: existingAgreementId // Preserve existing mapped ID if it exists
    };

    // Create Obligation if payments data exists
    // Check for both paymentsMaximum and paymentsTurnover (either can indicate payments exist)
    const hasPayments = (profile.paymentsMaximum != null && profile.paymentsMaximum > 0) || 
                        (profile.paymentsTurnover != null && profile.paymentsTurnover > 0);
    
    if (hasPayments) {
      profile.obligation = {
        obligationText: profile.paymentNatureDescription || '',
        willCreate: true,
        mappedObligationId: existingObligationId // Preserve existing mapped ID if it exists
      };

      profile.outgoingTransactionRule = {
        name: profile.paymentNatureDescription || '',
        cashFlow: 'OUTGOING',
        amountTo: profile.paymentsTurnover || 0,
        frequency: profile.paymentsEntries || 0,
        frequencyPeriod: 'YEAR',
        frequencyTo: profile.paymentsMaximum || profile.paymentsTurnover || 0,
        willCreate: true,
        mappedTransactionRuleId: existingOutgoingRuleId // Preserve existing mapped ID if it exists
      };
      console.log('Created obligation and outgoing rule for profile:', profile.clientName);
    } else {
      console.log('No obligation created - paymentsMaximum:', profile.paymentsMaximum, 'paymentsTurnover:', profile.paymentsTurnover);
    }

    // Create Rights if receipts data exists and receipt nature description is available
    // Check for both receiptMaximum and receiptTurnover (either can indicate receipts exist)
    const hasReceipts = (profile.receiptMaximum != null && profile.receiptMaximum > 0) || 
                        (profile.receiptTurnover != null && profile.receiptTurnover > 0);
    const hasReceiptDescription = profile.receiptNatureDescription && profile.receiptNatureDescription.trim() !== '';
    
    if (hasReceipts && hasReceiptDescription) {
      profile.rights = {
        rightsText: profile.receiptNatureDescription,
        willCreate: true,
        mappedRightsId: existingRightsId // Preserve existing mapped ID if it exists
      };

      profile.incomingTransactionRule = {
        name: profile.paymentNatureDescription || profile.receiptNatureDescription || '',
        cashFlow: 'INCOMING',
        amountTo: profile.receiptTurnover || 0,
        frequency: profile.receiptEntries || 0,
        frequencyPeriod: 'YEAR',
        frequencyTo: profile.receiptMaximum || profile.receiptTurnover || 0,
        willCreate: true,
        mappedTransactionRuleId: existingIncomingRuleId // Preserve existing mapped ID if it exists
      };
      console.log('Created rights and incoming rule for profile:', profile.clientName);
    } else {
      console.log('No rights created - receiptMaximum:', profile.receiptMaximum, 
                  'receiptTurnover:', profile.receiptTurnover, 
                  'receiptNatureDescription:', profile.receiptNatureDescription);
    }
  }

  /**
   * Clear domain mappings when disabled
   */
  /**
   * Clear domain mappings when disabling a profile
   * IMPORTANT: Preserves mapped IDs (mappedAgreementId, etc.) to maintain the "migrated" status
   * These IDs represent relationships to already-imported entities and should never be cleared
   */
  private clearDomainMappings(profile: TransactionProfileImportedData) {
    // Preserve mapped IDs before clearing mappings
    const mappedAgreementId = profile.agreement && profile.agreement.mappedAgreementId;
    const mappedObligationId = profile.obligation && profile.obligation.mappedObligationId;
    const mappedRightsId = profile.rights && profile.rights.mappedRightsId;
    const mappedOutgoingRuleId = profile.outgoingTransactionRule && profile.outgoingTransactionRule.mappedTransactionRuleId;
    const mappedIncomingRuleId = profile.incomingTransactionRule && profile.incomingTransactionRule.mappedTransactionRuleId;
    
    // Clear the domain mapping objects
    profile.agreement = undefined;
    profile.obligation = undefined;
    profile.rights = undefined;
    profile.outgoingTransactionRule = undefined;
    profile.incomingTransactionRule = undefined;
    
    // Restore mapped IDs if they existed (preserve import status)
    if (mappedAgreementId) {
      profile.agreement = {
        description: '',
        willCreate: false,
        mappedAgreementId: mappedAgreementId
      };
    }
    if (mappedObligationId) {
      profile.obligation = {
        obligationText: '',
        willCreate: false,
        mappedObligationId: mappedObligationId
      };
    }
    if (mappedRightsId) {
      profile.rights = {
        rightsText: '',
        willCreate: false,
        mappedRightsId: mappedRightsId
      };
    }
    if (mappedOutgoingRuleId) {
      profile.outgoingTransactionRule = {
        name: '',
        cashFlow: 'OUTGOING',
        amountTo: 0,
        frequency: 0,
        frequencyPeriod: 'YEAR',
        frequencyTo: 0,
        willCreate: false,
        mappedTransactionRuleId: mappedOutgoingRuleId
      };
    }
    if (mappedIncomingRuleId) {
      profile.incomingTransactionRule = {
        name: '',
        cashFlow: 'INCOMING',
        amountTo: 0,
        frequency: 0,
        frequencyPeriod: 'YEAR',
        frequencyTo: 0,
        willCreate: false,
        mappedTransactionRuleId: mappedIncomingRuleId
      };
    }
  }

  /**
   * Get all counterparties from all clients
   * Counterparties are stored in the same entityImportedDataList but have isClient = false or null
   */
  /**
   * Update cached counterparties list
   * Preserves array reference when possible to maintain PrimeNG DataTable pagination
   */
  private updateCachedCounterparties() {
    if (!this.importData || !this.importData.entityImportedDataList) {
      this.cachedCounterparties = [];
      return;
    }
    
    // Store current page before potentially creating new array
    const currentPage = this.counterpartiesTableFirst;
    
    const allCounterparties: EntityImportedData[] = [];
    const counterpartyMap = new Map<string, EntityImportedData>(); // Use name as key to avoid duplicates
    
    // Counterparties are in the same list but have isClient = false or null
    // Clients have isClient = true
    this.importData.entityImportedDataList.forEach(entity => {
      // Filter for entities that are NOT clients (isClient is false or null/undefined)
      if (entity.isClient !== true) {
        const key = this.getEntityKeyForMapping(entity);
        if (key && !counterpartyMap.has(key)) {
          counterpartyMap.set(key, entity);
          // Add computed property for sorting by display name
          (entity as any).excelCounterpartyName = this.getEntityDisplayName(entity) || '';
          allCounterparties.push(entity);
        }
      }
    });
    
    // Check if we can update in place (same entity objects, just property changes)
    // When excluding/enabling a counterparty, the entities are the same objects, just with updated properties
    // Create a set of existing entity object references for comparison
    const existingEntitySet = new Set(this.cachedCounterparties);
    
    // Check if all new entities are the same object references as existing ones
    // and the arrays have the same length (no entities added/removed)
    const sameEntityObjects = allCounterparties.length === this.cachedCounterparties.length &&
      allCounterparties.every(entity => existingEntitySet.has(entity)) &&
      this.cachedCounterparties.every(entity => {
        const key = this.getEntityKeyForMapping(entity);
        return key && allCounterparties.some(newEntity => 
          this.getEntityKeyForMapping(newEntity) === key && newEntity === entity
        );
      });
    
    if (sameEntityObjects && this.cachedCounterparties.length > 0) {
      // Same entity objects, just update computed properties in place to preserve array reference
      // This prevents PrimeNG from resetting pagination
      // Update computed properties on existing entities (preserving array reference and order)
      this.cachedCounterparties.forEach(entity => {
        // Update computed property for sorting (if needed)
        (entity as any).excelCounterpartyName = this.getEntityDisplayName(entity) || '';
        // Note: excludedFromImport and other properties are already updated on the entity object itself
        // since we're working with references to the same objects from entityImportedDataList
      });
      // Array reference is preserved, so pagination won't reset - no need to restore
    } else {
      // Must create new array (entities added/removed or first time)
      this.cachedCounterparties = allCounterparties;
      // Restore pagination after array reference change
      setTimeout(() => {
        this.counterpartiesTableFirst = currentPage;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  getAllCounterparties(): EntityImportedData[] {
    // Return cached list (used for counts and other operations)
    return this.cachedCounterparties;
  }
  
  /**
   * Update cached bank accounts list with entity information
   */
  private updateCachedBankAccounts() {
    if (!this.importData || !this.importData.entityImportedDataList) {
      this.cachedBankAccounts = [];
      return;
    }
    
    const bankAccountsWithEntity: any[] = [];
    
    // Iterate through all entities (clients and counterparties)
    this.importData.entityImportedDataList.forEach(entity => {
      if (entity.bankAccounts && entity.bankAccounts.length > 0) {
        const entityName = entity.isClient === true 
          ? (entity.excelClientName || this.getEntityDisplayName(entity))
          : this.getEntityDisplayName(entity);
        const entityType = entity.isClient === true ? 'Client' : 'Counterparty';
        const isExcluded = entity.excludedFromImport === true;
        
        entity.bankAccounts.forEach(account => {
          bankAccountsWithEntity.push({
            account: account,
            accountName: account.accountName || '',
            bankInstitutionName: account.bankInstitutionName || '',
            entityName: entityName || '',
            entityType: entityType,
            isExcluded: isExcluded,
            entity: entity
          });
        });
      }
    });
    
    this.cachedBankAccounts = bankAccountsWithEntity;
  }
  
  /**
   * Get all bank accounts with entity information
   */
  getAllBankAccounts(): any[] {
    return this.cachedBankAccounts;
  }
  
  /**
   * Get count of enabled bank accounts
   */
  getEnabledBankAccountsCount(): number {
    return this.cachedBankAccounts.filter(ba => !ba.isExcluded).length;
  }
  
  /**
   * Get count of disabled bank accounts
   */
  getDisabledBankAccountsCount(): number {
    return this.cachedBankAccounts.filter(ba => ba.isExcluded).length;
  }
  
  /**
   * Handle bank accounts table sorting
   */
  onBankAccountsSort(event: any) {
    const sortField = event.field;
    this.bankAccountsSortField = sortField;
    this.bankAccountsSortOrder = event.order;
    
    console.log('Sorting bank accounts by:', sortField, 'order:', event.order === 1 ? 'asc' : 'desc');
  }

  /**
   * Get entity key for mapping (used to identify unique counterparties)
   */
  private getEntityKeyForMapping(entity: EntityImportedData): string {
    if (entity.organisation && entity.organisation.name) {
      return entity.organisation.name.trim().toLowerCase();
    }
    if (entity.person) {
      const name = (entity.person.firstNames && entity.person.lastNames ? 
                    `${entity.person.firstNames} ${entity.person.lastNames}` : 
                    entity.person.callingName);
      return name ? name.trim().toLowerCase() : null;
    }
    return null;
  }

  /**
   * Search for entities when user types in the search box (for counterparties)
   */
  searchEntity(event, entity: EntityImportedData) {
    const query = event.query || '';
    if (!query || query.length < 2) {
      // Clear results for this entity
      const key = this.getEntityKeyForMapping(entity);
      if (key) {
        this.selectedEntityMapping.set(key, {
          entity: entity,
          results: []
        });
      }
      return;
    }
    this.genericSearchService.getHeaderSearchResults(query, ConstantsService.HEADER_SEARCH)
      .subscribe(response => {
        // HEADER_SEARCH returns { arrResult: { persons: [...], organisations: [...] } }
        const arrResult = response.arrResult || {};
        const personResults = this.parsePersonSearchResponse(arrResult.persons || []);
        const orgResults = this.parseOrganisationSearchResponse(arrResult.organisations || []);
        const results = personResults.concat(orgResults);
        
        // Store results for this specific entity
        const key = this.getEntityKeyForMapping(entity);
        if (key) {
          this.selectedEntityMapping.set(key, {
            entity: entity,
            results: results
          });
        }
      });
  }

  /**
   * Handle entity selection from search results (for counterparties)
   */
  selectEntity(selectedResult: any, entity: EntityImportedData) {
    if (selectedResult && selectedResult.entityId) {
      entity.mappedEntityId = selectedResult.entityId;
      entity.mappedEntityName = selectedResult.value;
      entity.entitySearchQuery = ''; // Clear search query
      const entityName = (entity.organisation && entity.organisation.name) || 
                        (entity.person && entity.person.firstNames && entity.person.lastNames ? 
                         `${entity.person.firstNames} ${entity.person.lastNames}` : 
                         (entity.person && entity.person.callingName));
      console.log('Mapped entity:', entityName, '->', entity.mappedEntityName);
      
      // Trigger change detection to update counts
      this.updateCounts();
      
      // Emit mapping change event to save to session
      if (this.onMappingChange) {
        this.onMappingChange();
      }
      this.mappingChange.emit(this.importData);
    }
  }

  /**
   * Clear entity mapping (for counterparties)
   */
  clearEntityMapping(entity: EntityImportedData) {
    entity.mappedEntityId = null;
    entity.mappedEntityName = null;
    
    // Trigger change detection to update counts
    this.updateCounts();
    
    // Emit mapping change event to save to session
    if (this.onMappingChange) {
      this.onMappingChange();
    }
    this.mappingChange.emit(this.importData);
  }

  /**
   * Get search results for a specific entity (for counterparties)
   */
  getSearchResultsForEntityMapping(entity: EntityImportedData): any[] {
    const key = this.getEntityKeyForMapping(entity);
    if (key) {
      const mapping = this.selectedEntityMapping.get(key);
      return mapping ? mapping.results : [];
    }
    return [];
  }

  /**
   * Parse person search response (for counterparties)
   */
  private parsePersonSearchResponse(personResponse: any[]): any[] {
    const arrFormatPersonSearchResults: any[] = [];
    if (personResponse && personResponse.length > 0) {
      for (let resultCounter = 0; resultCounter < personResponse.length; resultCounter++) {
        const searchRecordData = personResponse[resultCounter];
        // Build full name from person data
        let personName = '';
        if (searchRecordData.wholeName && searchRecordData.wholeName.trim() !== '') {
          personName = searchRecordData.wholeName.trim();
        } else if (searchRecordData.firstname && searchRecordData.lastname) {
          personName = `${searchRecordData.firstname} ${searchRecordData.lastname}`.trim();
        } else if (searchRecordData.callingname) {
          personName = searchRecordData.callingname.trim();
        }
        
        if (personName) {
          arrFormatPersonSearchResults.push({
            'value': personName,
            'id': searchRecordData.id,
            'entityId': searchRecordData.entityId,
            'clientId': searchRecordData.clientId,
            'type': 'person'
          });
        }
      }
    }
    return arrFormatPersonSearchResults;
  }

  /**
   * Get count of existing counterparties
   */
  getExistingCounterparties(): number {
    const counterparties = this.getAllCounterparties();
    return counterparties.filter(cp => cp.exists || cp.mappedEntityId).length;
  }

  /**
   * Get display name for an entity (safely handles null organisation/person)
   */
  getEntityDisplayName(entity: EntityImportedData): string {
    if (!entity) {
      return '';
    }
    if (entity.organisation && entity.organisation.name) {
      return entity.organisation.name;
    }
    if (entity.person) {
      if (entity.person.firstNames && entity.person.lastNames) {
        return entity.person.firstNames + ' ' + entity.person.lastNames;
      }
      if (entity.person.callingName) {
        return entity.person.callingName;
      }
    }
    return '';
  }

  /**
   * Handle client exclusion toggle
   */
  toggleClientExclusion(client: EntityImportedData, event: any) {
    const currentlyExcluded = client.excludedFromImport === true;
    const willBeExcluded = !event.target.checked;
    
    if (willBeExcluded && !currentlyExcluded) {
      // User is trying to exclude - prevent default and show confirmation dialog
      event.preventDefault();
      event.stopPropagation();
      this.openExcludeClientDialog(client);
    } else if (!willBeExcluded && currentlyExcluded) {
      // User is including client - no confirmation needed, allow default behavior
      client.excludedFromImport = false;
      this.updateCachedBankAccounts(); // Update bank accounts cache
      this.emitMappingChange();
    }
    // If state is already what user wants, allow default behavior
  }

  /**
   * Open confirmation dialog for excluding a client
   */
  openExcludeClientDialog(client: EntityImportedData) {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, config);
    this.dialogRef.componentInstance.message = 
      `Are you sure you want to exclude "${this.getEntityDisplayName(client)}" from the import? ` +
      `All related transaction profiles for this client will be disabled and removed from the system.`;
    this.dialogRef.componentInstance.title = 'Exclude Client from Import';
    
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // User confirmed - exclude the client
        this.excludeClient(client);
      }
      // If cancelled, checkbox state is already reverted in toggleClientExclusion
    });
  }

  /**
   * Exclude client and disable all related transaction profiles
   */
  excludeClient(client: EntityImportedData) {
    client.excludedFromImport = true;
    
    // Update bank accounts cache since entity exclusion changed
    this.updateCachedBankAccounts();
    
    // Get client name for matching
    const clientName = client.excelClientName || this.getEntityDisplayName(client);
    
    // Disable all transaction profiles for this client
    if (this.importData && this.importData.transactionProfiles) {
      let disabledCount = 0;
      this.importData.transactionProfiles.forEach(profile => {
        if (profile.clientName === clientName) {
          profile.enabled = false;
          this.clearDomainMappings(profile);
          disabledCount++;
        }
      });
      console.log(`Disabled ${disabledCount} transaction profiles for excluded client: ${clientName}`);
    }
    
    this.emitMappingChange();
  }

  /**
   * Emit mapping change event
   */
  private emitMappingChange() {
    // Update cached counterparties when data changes
    this.updateCachedCounterparties();
    // Re-validate counterparties in transaction profiles when entities change
    this.validateCounterpartiesInProfiles();
    if (this.onMappingChange) {
      this.onMappingChange();
    }
    this.mappingChange.emit(this.importData);
  }

  /**
   * Extract unique counterparties from transaction profiles and show them in a dialog
   */
  filterCounterParties() {
    try {
      console.log('=== filterCounterParties() called ===');
      console.log('importData:', this.importData);
      console.log('transactionProfiles:', this.importData && this.importData.transactionProfiles);
      console.log('entityImportedDataList:', this.importData && this.importData.entityImportedDataList);
      
      if (!this.importData || !this.importData.transactionProfiles || !this.importData.entityImportedDataList) {
        console.log('filterCounterParties: Missing import data');
        console.log('importData exists:', !!this.importData);
        console.log('transactionProfiles exists:', !!(this.importData && this.importData.transactionProfiles));
        console.log('entityImportedDataList exists:', !!(this.importData && this.importData.entityImportedDataList));
        return;
      }

      // Extract unique counter party names from transaction profiles
      const usedCounterPartyNames = new Map<string, string>(); // Map<lowercase, original>
      console.log('Processing transaction profiles, count:', this.importData.transactionProfiles.length);
      
      this.importData.transactionProfiles.forEach((profile, index) => {
        console.log(`Profile ${index}:`, {
          clientName: profile.clientName,
          counterPartyName: profile.counterPartyName,
          enabled: profile.enabled
        });
        
        if (profile.counterPartyName && profile.counterPartyName.trim() !== '') {
          const cpNameOriginal = profile.counterPartyName.trim();
          const cpNameLower = cpNameOriginal.toLowerCase();
          if (!usedCounterPartyNames.has(cpNameLower)) {
            usedCounterPartyNames.set(cpNameLower, cpNameOriginal);
          }
        }
      });
      
      const usedCounterPartyNamesList: string[] = Array.from(usedCounterPartyNames.values()).sort();
      console.log('=== Counter Parties Used in Transaction Profiles ===');
      console.log('Total unique counter parties in profiles:', usedCounterPartyNames.size);
      console.log('Counter party names (sorted):', usedCounterPartyNamesList);

      // Get all clients from current entity list
      const clients: EntityImportedData[] = [];
      const clientNames = new Set<string>();
      const clientNamesList: string[] = [];
      console.log('Processing entity list, count:', this.importData.entityImportedDataList.length);
      
      this.importData.entityImportedDataList.forEach((entity, index) => {
        if (entity.isClient === true) {
          clients.push(entity);
          const clientName = entity.excelClientName || this.getEntityDisplayName(entity);
          if (clientName) {
            const clientNameLower = clientName.trim().toLowerCase();
            if (!clientNames.has(clientNameLower)) {
              clientNames.add(clientNameLower);
              clientNamesList.push(clientName.trim());
            }
          }
        }
      });
      console.log('=== Clients (will be kept) ===');
      console.log('Total clients:', clients.length);
      console.log('Client names (sorted):', clientNamesList.sort());

      // Get all counterparties currently registered as entities
      const allCounterparties: EntityImportedData[] = [];
      const counterpartyNamesList: string[] = [];
      const counterpartyMap = new Map<string, EntityImportedData>(); // Map<lowercase name, entity>
      
      this.importData.entityImportedDataList.forEach(entity => {
        if (entity.isClient !== true) {
          allCounterparties.push(entity);
          const entityName = this.getEntityDisplayName(entity);
          if (entityName) {
            const entityNameLower = entityName.trim().toLowerCase();
            counterpartyMap.set(entityNameLower, entity);
            counterpartyNamesList.push(entityName.trim());
          }
        }
      });
      console.log('=== Counter Parties Currently Registered as Entities ===');
      console.log('Total counterparties (non-clients):', allCounterparties.length);
      console.log('Counterparty entity names (sorted):', counterpartyNamesList.sort());

      // Extract counterparties from transaction profiles
      // Match existing entities or create new ones
      const extractedCounterparties: EntityImportedData[] = [];
      const extractedNamesList: string[] = [];
      
      usedCounterPartyNames.forEach((originalName, lowercaseName) => {
        // Check if this counterparty name matches a client (skip if it does)
        if (clientNames.has(lowercaseName)) {
          console.log(`Skipping "${originalName}" - it is a client`);
          return;
        }
        
        // Try to find existing entity for this counterparty
        let counterpartyEntity = counterpartyMap.get(lowercaseName);
        
        if (!counterpartyEntity) {
          // Create new entity for this counterparty
          counterpartyEntity = {
            isClient: false,
            exists: false,
            organisation: {
              name: originalName,
              id: null,
              entityId: null,
              isin: null,
              legalValue: null,
              stockExchangeValue: null,
              incorporatedDate: null,
              registrationCode: null,
              logo: null,
              sanctionStatus: null,
              specialFinancialInstitution: false,
              userId: null,
              activityCategory: null,
              activities: [],
              activitySummary: null,
              clientId: null,
              bankAccounts: [],
              addresses: []
            },
            person: null,
            counterParties: [],
            bankAccounts: [],
            excludedFromImport: false
          };
          console.log(`Created new entity for counterparty: "${originalName}"`);
        } else {
          console.log(`Found existing entity for counterparty: "${originalName}"`);
        }
        
        extractedCounterparties.push(counterpartyEntity);
        extractedNamesList.push(originalName);
      });
      
      // Sort extracted counterparties by name
      extractedCounterparties.sort((a, b) => {
        const nameA = this.getEntityDisplayName(a).toLowerCase();
        const nameB = this.getEntityDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      // Update extracted names list to match sorted order
      extractedNamesList.length = 0;
      extractedCounterparties.forEach(cp => {
        extractedNamesList.push(this.getEntityDisplayName(cp));
      });
      
      console.log('=== Extracted Counter Parties (will replace current list) ===');
      console.log('Total extracted counterparties:', extractedCounterparties.length);
      console.log('Extracted counterparty names (sorted):', extractedNamesList);

      // Store extracted counterparties for later use in save()
      this.extractedCounterparties = extractedCounterparties;

      // Show dialog with list of extracted counterparties
      console.log('Opening filter dialog with', extractedCounterparties.length, 'counterparties');
      this.openFilterCounterpartiesDialog(extractedCounterparties);
    } catch (error) {
      console.error('Error in filterCounterParties:', error);
      console.error('Error stack:', error.stack);
    }
  }

  /**
   * Open dialog to show extracted counterparties
   */
  openFilterCounterpartiesDialog(extractedCounterparties: EntityImportedData[]) {
    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;
    config.width = '600px';

    this.dialogRef = this.dialog.open(FilterCounterpartiesDialogComponent, config);
    this.dialogRef.componentInstance.counterpartiesToFilter = extractedCounterparties;
    this.dialogRef.componentInstance.getEntityDisplayName = (entity: EntityImportedData) => this.getEntityDisplayName(entity);
    
    this.dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // User confirmed - extracted counterparties are already stored in this.extractedCounterparties
        // They will be applied when save() is called
        console.log('User confirmed extraction. Counterparties will be applied on save.');
      } else {
        // User cancelled - clear the extracted counterparties
        this.extractedCounterparties = [];
        console.log('User cancelled extraction.');
      }
    });
  }

  /**
   * Handle counterparty exclusion toggle
   */
  toggleCounterpartyExclusion(counterparty: EntityImportedData, event: any) {
    const currentlyExcluded = counterparty.excludedFromImport === true;
    const willBeExcluded = !event.target.checked;
    
    if (willBeExcluded && !currentlyExcluded) {
      // User is trying to exclude - prevent default and show confirmation dialog
      event.preventDefault();
      event.stopPropagation();
      this.openExcludeCounterpartyDialog(counterparty);
    } else if (!willBeExcluded && currentlyExcluded) {
      // User is including counterparty - no confirmation needed, allow default behavior
      counterparty.excludedFromImport = false;
      this.updateCachedBankAccounts(); // Update bank accounts cache
      this.emitMappingChange();
    }
    // If state is already what user wants, allow default behavior
  }

  /**
   * Open confirmation dialog for excluding a counterparty
   */
  openExcludeCounterpartyDialog(counterparty: EntityImportedData) {
    // Store current page number before opening dialog
    // Note: We'll restore this after the dialog closes to prevent pagination reset
    
    // Find all ENABLED transaction profiles that use this counterparty (only these will be disabled)
    const affectedProfiles: TransactionProfileImportedData[] = [];
    if (this.importData && this.importData.transactionProfiles) {
      const counterpartyName = this.getEntityDisplayName(counterparty);
      this.importData.transactionProfiles.forEach(profile => {
        // Only include profiles that are currently enabled (they will be disabled)
        if (profile.enabled === true && 
            profile.counterPartyName && 
            profile.counterPartyName.trim().toLowerCase() === counterpartyName.trim().toLowerCase()) {
          affectedProfiles.push(profile);
        }
      });
    }

    const config = new MdDialogConfig();
    config.disableClose = true;
    config.viewContainerRef = this.viewContainerRef;

    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, config);
    
    let message = `Are you sure you want to exclude "${this.getEntityDisplayName(counterparty)}" from the import?`;
    if (affectedProfiles.length > 0) {
      message += ` The following transaction profile${affectedProfiles.length > 1 ? 's' : ''} will be disabled: `;
      const profileList = affectedProfiles.map((profile, index) => {
        return `${index + 1}. ${profile.clientName} - ${profile.counterPartyName} (${profile.currencyIsoCode})`;
      }).join(', ');
      message += profileList + '.';
    } else {
      message += ` No enabled transaction profiles will be affected.`;
    }
    
    this.dialogRef.componentInstance.message = message;
    this.dialogRef.componentInstance.title = 'Exclude Counterparty from Import';
    
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // User confirmed - exclude the counterparty
        // Note: excludeCounterparty will handle pagination preservation
        this.excludeCounterparty(counterparty);
      }
      // If cancelled, checkbox state is already reverted in toggleCounterpartyExclusion
    });
  }

  /**
   * Exclude counterparty and disable all related transaction profiles
   */
  excludeCounterparty(counterparty: EntityImportedData) {
    // Store current page before making changes
    const currentPage = this.counterpartiesTableFirst;
    
    counterparty.excludedFromImport = true;
    
    // Update bank accounts cache since entity exclusion changed
    this.updateCachedBankAccounts();
    
    // Get counterparty name for matching
    const counterpartyName = this.getEntityDisplayName(counterparty);
    
    // Disable all ENABLED transaction profiles for this counterparty
    if (this.importData && this.importData.transactionProfiles) {
      let disabledCount = 0;
      this.importData.transactionProfiles.forEach(profile => {
        // Only disable profiles that are currently enabled
        if (profile.enabled === true &&
            profile.counterPartyName && 
            profile.counterPartyName.trim().toLowerCase() === counterpartyName.trim().toLowerCase()) {
          profile.enabled = false;
          this.clearDomainMappings(profile);
          disabledCount++;
        }
      });
      console.log(`Disabled ${disabledCount} transaction profiles for excluded counterparty: ${counterpartyName}`);
    }
    
    this.updateCounts();
    // emitMappingChange() will call updateCachedCounterparties() synchronously, which creates a new array reference
    // This causes PrimeNG to reset pagination. We need to restore it AFTER emitMappingChange() completes.
    this.emitMappingChange();
    
    // Restore pagination after ALL synchronous updates and Angular change detection complete
    // Use setTimeout with a small delay to ensure it runs after Angular's change detection cycle
    // This ensures the DataTable has finished processing the new array reference before we restore pagination
    setTimeout(() => {
      this.counterpartiesTableFirst = currentPage;
      // Trigger change detection to ensure the pagination update is reflected
      this.cdr.detectChanges();
    }, 10);
  }

}


// WEBPACK FOOTER //
// ./src/app/admin/import/review/review-import.component.ts